import { Divider, IFormRender, Justify, Label, Row, TextFormField } from '@busybusy/webapp-react-ui';
import EquipmentInspectionChecklistPickerFormField from 'components/domain/equipment/EquipmentInspectionChecklistPickerFormField/EquipmentInspectionChecklistPickerFormField';
import { t } from 'utils/localize';
import v_integers_only from 'utils/validations/v_number_integers_only';
import v_require_conditional from 'utils/validations/v_require_conditional';
import { RoutineInspectionFormFields, buildRoutineFieldName, getRoutineRowCount } from './routineInspectionFormUtils';
import './RoutineInspectionFields.scss';

const ROUTINE_INTERVAL_MIN = 1;
const ROUTINE_INTERVAL_MAX = 9999;

function v_interval_range(value: string) {
  return {
    message: t('Interval must be between {{min}} and {{max}} days', {
      min: ROUTINE_INTERVAL_MIN,
      max: ROUTINE_INTERVAL_MAX,
    }),
    validate: () => {
      if (!value) return true;
      const n = Number(value);
      if (!Number.isInteger(n)) return true;
      return n >= ROUTINE_INTERVAL_MIN && n <= ROUTINE_INTERVAL_MAX;
    },
  };
}

function v_no_duplicate_routine(
  value: string,
  args: {
    rowNumber: number;
    checklistId: string | null | undefined;
    formData: RoutineInspectionFormFields;
  }
) {
  return {
    message: t('A routine inspection with this checklist and interval already exists'),
    validate: () => {
      const { rowNumber, checklistId, formData } = args;
      if (!checklistId || !value) return true;
      const n = Number(value);
      if (!Number.isInteger(n) || n < ROUTINE_INTERVAL_MIN || n > ROUTINE_INTERVAL_MAX) return true;

      const rowCount = getRoutineRowCount(formData);
      for (let row = 1; row <= rowCount; row++) {
        if (row === rowNumber) continue;
        const otherChecklistId = formData[buildRoutineFieldName('checklistId', row)];
        const otherIntervalStr = formData[buildRoutineFieldName('interval', row)];
        if (
          otherChecklistId === checklistId &&
          otherIntervalStr != null &&
          otherIntervalStr !== '' &&
          Number(otherIntervalStr) === n
        ) {
          return false;
        }
      }
      return true;
    },
  };
}

export interface IRoutineInspectionFieldsProps<T extends RoutineInspectionFormFields> {
  form: IFormRender<T>;
}

/**
 * Dynamic list of routine-inspection rows (checklist + recurring-day interval) shared by the
 * equipment create and edit forms. Renders at least EQUIPMENT_MAX_ROUTINE_INSPECTIONS rows, growing
 * to show any extra existing routines beyond the cap (getRoutineRowCount), bound to flat form fields
 * named via buildRoutineFieldName; the interval is required only once a row's checklist is selected,
 * and empty rows are ignored on submit (getRoutineItemsFromFormData). Submission past the cap is
 * blocked by the parent form (exceedsRoutineInspectionLimit). The surrounding section
 * header/wrapper is left to the parent form.
 */
export default function RoutineInspectionFields<FormType extends RoutineInspectionFormFields>(
  props: IRoutineInspectionFieldsProps<FormType>
) {
  const { form } = props;
  const formErrors = form.state.errors as Record<string, { message: string } | undefined>;

  return (
    <>
      <Row justify={Justify.SPACE_BETWEEN}>
        <Label>{t('Inspection')}</Label>
        <Label>{t('Interval (Days)')}</Label>
      </Row>
      <Divider className={'mb-4'} />
      {Array.from({ length: getRoutineRowCount(form.state.data) }, (_, index) => {
        const rowNumber = index + 1;
        const checklistFieldName = buildRoutineFieldName('checklistId', rowNumber);
        const intervalFieldName = buildRoutineFieldName('interval', rowNumber);
        const checklistId = form.state.data[checklistFieldName];
        const intervalErrorMessage = formErrors[intervalFieldName]?.message;
        const isShortError = intervalErrorMessage === t('Required field');
        const showFullWidthError = !!intervalErrorMessage && !isShortError;

        return (
          <div key={rowNumber}>
            <Row justify={Justify.SPACE_BETWEEN}>
              <EquipmentInspectionChecklistPickerFormField
                name={checklistFieldName}
                form={form}
                className={'routine-checklist-picker'}
              />
              <TextFormField
                name={intervalFieldName}
                form={form}
                className={`routine-interval-field${showFullWidthError ? ' hide-inline-error' : ''}`}
                revalidateOnNewValidations={true}
                restrictTo={{ maxLength: 4 }}
                validations={[
                  {
                    validation: v_require_conditional,
                    args: { conditionalValue: form.state.data[checklistFieldName] },
                  },
                  { validation: v_integers_only, args: { message: t('Interval must contain only whole numbers') } },
                  { validation: v_interval_range },
                  {
                    validation: v_no_duplicate_routine,
                    args: { rowNumber, checklistId, formData: form.state.data },
                  },
                ]}
              />
            </Row>
            {showFullWidthError && <p className="routine-interval-full-error">{intervalErrorMessage}</p>}
          </div>
        );
      })}
    </>
  );
}
